/**
 * Utility functions for name parsing and transformation
 */

/**
 * Extract initials from a full name (max 2 characters)
 * @param name - Full name string
 * @returns Uppercase initials (e.g., "John Doe" → "JD", "Alice" → "AL")
 */
export const getInitials = (name: string): string => {
  if (!name || typeof name !== 'string') return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) {
    // Single name - take first two characters
    return parts[0].slice(0, 2).toUpperCase();
  }
  // Multiple names - take first character of first and last
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Format a variable name for display (snake_case to Title Case)
 * Replaces underscores with spaces and capitalizes first letter of each word.
 *
 * @param varName - Variable name in snake_case or camelCase
 * @returns Formatted display name
 *
 * @example
 * formatVariableName('first_name') // "First Name"
 * formatVariableName('company_size') // "Company Size"
 * formatVariableName('ceo_name') // "Ceo Name"
 */
export const formatVariableName = (varName: string): string => {
  return varName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export interface NameParts {
  first: string;
  last: string;
  full: string;
}

/**
 * Extract first and last name from a full name
 * First name = first word
 * Last name = everything after first name, including middle names and suffixes
 *
 * Examples:
 * - "John Doe" → first: "John", last: "Doe"
 * - "John Doe Jr." → first: "John", last: "Doe Jr."
 * - "John Smith III" → first: "John", last: "Smith III"
 * - "Mary Jane O'Brien" → first: "Mary", last: "Jane O'Brien"
 *
 * Note: This simple approach works for most business use cases.
 * Middle names and suffixes are included in the last name field.
 */
export const parseFullName = (fullName: string): NameParts => {
  const trimmed = (fullName || '').trim();
  const parts = trimmed.split(/\s+/).filter(part => part.length > 0);

  if (parts.length === 0) {
    return { first: '', last: '', full: trimmed };
  } else if (parts.length === 1) {
    // Only one name - use it as first name
    return { first: parts[0], last: '', full: trimmed };
  } else {
    // Multiple names - first word is first name
    // Everything else is last name (includes middle names and suffixes)
    const first = parts[0];
    const last = parts.slice(1).join(' ');

    return { first, last, full: trimmed };
  }
};

/**
 * Apply transform to a string value
 * Supported transforms: .first, .last, .title, .upper, .lower, .default:"value"
 */
export const applyTransform = (value: string, transform: string): string => {
  if (!value) {
    // Handle default transform for empty values
    const defaultMatch = transform.match(/\.default:"([^"]*)"/);
    if (defaultMatch) {
      return defaultMatch[1];
    }
    return '';
  }

  // Name part transforms
  if (transform === '.first') {
    return parseFullName(value).first;
  }
  if (transform === '.last') {
    return parseFullName(value).last;
  }

  // Case transforms
  if (transform === '.title') {
    return value
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  if (transform === '.upper') {
    return value.toUpperCase();
  }
  if (transform === '.lower') {
    return value.toLowerCase();
  }

  // Default value transform
  const defaultMatch = transform.match(/\.default:"([^"]*)"/);
  if (defaultMatch) {
    return value || defaultMatch[1];
  }

  return value;
};

/**
 * Parse variable token with transforms
 * Example: {{ceo_name.first}} -> { varName: 'ceo_name', transform: '.first' }
 */
export interface ParsedVariable {
  varName: string;
  transform?: string;
  fullToken: string;
}

// Safe regex patterns with quantifier limits to prevent ReDoS attacks
const SAFE_PATTERNS = {
  // Updated to support spaces and special characters in variable names (for enriched columns)
  VARIABLE_NAME: '[^{}]{1,200}',
  TRANSFORM: '\\.[a-zA-Z_]{1,20}(?::"[^"]{0,200}")?',
  FULL_TOKEN: '\\{\\{\\s*([^{}]{1,200}?)(\\.[a-zA-Z_]{1,20}(?::"[^"]{0,200}")?)?\\s*\\}\\}'
} as const;

// Maximum input length to prevent DOS attacks
const MAX_INPUT_LENGTH = 100000;

export const parseVariableToken = (token: string): ParsedVariable | null => {
  // Input validation
  if (!token || typeof token !== 'string' || token.length > 1000) {
    return null;
  }

  // Match {{variable}} or {{variable.transform}} with safe pattern
  const match = token.match(new RegExp(`^${SAFE_PATTERNS.FULL_TOKEN}$`));

  if (!match) {
    return null;
  }

  return {
    varName: match[1],
    transform: match[2],
    fullToken: token
  };
};

/**
 * Extract all variable tokens from text
 * SECURITY: Protected against ReDoS attacks with input length limits and safe regex patterns
 */
export const extractVariableTokens = (text: string): ParsedVariable[] => {
  // Input validation to prevent ReDoS
  if (!text || typeof text !== 'string') return [];
  if (text.length > MAX_INPUT_LENGTH) {
    console.warn(`Input text too large (${text.length} chars), truncating to ${MAX_INPUT_LENGTH}`);
    text = text.substring(0, MAX_INPUT_LENGTH);
  }

  const tokens: ParsedVariable[] = [];
  const regex = new RegExp(SAFE_PATTERNS.FULL_TOKEN, 'g');

  let match;
  while ((match = regex.exec(text)) !== null) {
    tokens.push({
      varName: match[1].trim(), // Trim whitespace from variable names
      transform: match[2],
      fullToken: match[0]
    });
  }

  return tokens;
};

/**
 * Auto-generate first/last name variables from available data
 * SECURITY: Validates input types and prevents prototype pollution
 */
export const generateNameVariables = (data: Record<string, any>): Record<string, string> => {
  const generated: Record<string, string> = {};

  // Look for common full name fields (including both lowercase and capitalized versions)
  const nameFields = new Set(['name', 'Name', 'full_name', 'fullName', 'contact_name', 'ceo_name', 'founder_name']);

  // Reserved/dangerous keys to prevent prototype pollution
  const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

  const safeSet = (key: string, value: string) => {
    // Validate key name to prevent prototype pollution
    if (DANGEROUS_KEYS.has(key) || key.includes('__')) {
      return;
    }
    // Don't overwrite existing data from the input
    if (!(key in data) && !(key in generated)) {
      generated[key] = value;
    }
  };

  for (const field of nameFields) {
    const fullName = data[field];

    // Strict type checking to prevent type confusion attacks
    if (typeof fullName !== 'string' || !fullName.trim()) {
      continue;
    }

    // Length validation to prevent DOS
    if (fullName.length > 500) {
      console.warn(`Name field ${field} exceeds maximum length (500 chars)`);
      continue;
    }

    const parsed = parseFullName(fullName);

    // Generate first_name and last_name variants
    let baseField = field.replace(/_name$/, '').replace(/Name$/, '');

    // Handle empty baseField case
    if (!baseField || baseField === field) {
      baseField = field;
    }

    if (parsed.first) {
      safeSet(`${baseField}_first`, parsed.first);
      safeSet(`${field}_first`, parsed.first);
      if (field === 'name' || field === 'Name') {
        safeSet('first_name', parsed.first);
      }
    }

    if (parsed.last) {
      safeSet(`${baseField}_last`, parsed.last);
      safeSet(`${field}_last`, parsed.last);
      if (field === 'name' || field === 'Name') {
        safeSet('last_name', parsed.last);
      }
    }
  }

  return generated;
};

/** A count of people, pluralized: "1 person", "4 people". */
export const countOfPeople = (n: number): string => `${n} ${n === 1 ? 'person' : 'people'}`;
